import React from "react";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, ChevronRight, Plus, Minus, MoveVertical, MoveHorizontal, ArrowLeft,
} from "lucide-react";

/**
 * Toolbar row above the calendar grid: day/week navigation, view-scope
 * toggle (Personal vs Global), and V/H zoom controls. Kept dumb — every
 * behaviour is driven from the parent so this file can be swapped in
 * isolation.
 *
 *  Props (day view):
 *    viewMode="day"                    → renders prev/today/next day buttons.
 *  Props (per-staff week view):
 *    viewMode="weekStaff"              → renders back-to-day + weekDates title + utilisation pill.
 */
export default function CalendarToolbar({
  viewMode,
  date,
  setDate,
  shiftDay,
  weekDates,
  weekStaffId,
  staffList,
  weekUtilisation,
  onExitWeekStaff,
  scope,
  personalMode,
  setViewScope,
  zoomV, zoomH, bumpZoom,
}) {
  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap min-w-0">
      {viewMode === "weekStaff" ? (
        <>
          <Button size="sm" variant="outline" onClick={onExitWeekStaff} data-testid="cal-back-to-day">
            <ArrowLeft className="w-4 h-4 mr-1" /> All staff
          </Button>
          <Button size="sm" variant="outline" onClick={() => shiftDay(-7)} data-testid="cal-prev-week"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setDate(new Date())} data-testid="cal-today-btn">This week</Button>
          <Button size="sm" variant="outline" onClick={() => shiftDay(7)} data-testid="cal-next-week"><ChevronRight className="w-4 h-4" /></Button>
          <div className="text-sm font-medium ml-3 flex items-center gap-2 flex-wrap" data-testid="week-view-title">
            {(() => {
              const s = weekDates[0], e = weekDates[6];
              const staff = staffList.find((x) => x.id === weekStaffId);
              const sameMonth = s.getMonth() === e.getMonth();
              const range = sameMonth
                ? `${s.getDate()} – ${e.getDate()} ${e.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`
                : `${s.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${e.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
              return <>{staff?.full_name || "Employee"} · <span className="text-muted-foreground">{range}</span></>;
            })()}
            {weekUtilisation && (() => {
              // Colour the pill so a glance tells the operator whether the
              // therapist is under-, well-, or over-booked. Under 40% is
              // "quiet" (muted), 40-79% is "healthy" (emerald), 80-99%
              // is "hot" (amber), and ≥100% flags an overbook (red).
              const pct = weekUtilisation.pct;
              const hours = (n) => `${Math.floor(n / 60)}h ${n % 60 ? String(n % 60).padStart(2, "0") + "m" : ""}`.trim();
              let cls = "bg-secondary text-secondary-foreground";
              let label = "—";
              if (pct == null) {
                label = "No available hours";
              } else {
                label = `${pct}% booked`;
                if (pct >= 100) cls = "bg-red-100 text-red-700 border border-red-200";
                else if (pct >= 80) cls = "bg-amber-100 text-amber-800 border border-amber-200";
                else if (pct >= 40) cls = "bg-emerald-100 text-emerald-800 border border-emerald-200";
                else cls = "bg-muted text-muted-foreground border border-border";
              }
              const title = pct == null
                ? "This staff is not scheduled to work any hours this week."
                : `${hours(weekUtilisation.booked)} booked of ${hours(weekUtilisation.available)} scheduled`;
              return (
                <span
                  className={"px-2 py-0.5 rounded-full text-[11px] font-medium " + cls}
                  data-testid="week-utilisation-pill"
                  title={title}
                >
                  {label}
                </span>
              );
            })()}
          </div>
        </>
      ) : (
        <>
          <Button size="sm" variant="outline" onClick={() => shiftDay(-1)} data-testid="cal-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setDate(new Date())} data-testid="cal-today-btn">Today</Button>
          <Button size="sm" variant="outline" onClick={() => shiftDay(1)} data-testid="cal-next-day"><ChevronRight className="w-4 h-4" /></Button>
          <div className="text-sm font-medium ml-3">
            {date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </>
      )}

      {/* View-scope toggle. Only appears when the caller holds BOTH
          Personal View and Global View on the Booking Calendar.
          Locked-in modes (personal-only or global-only) show a static
          pill so the user knows which lens they're in. */}
      {scope.canToggle ? (
        <div className="ml-3 inline-flex rounded-md border border-border overflow-hidden" data-testid="cal-view-scope-toggle">
          <button
            type="button"
            onClick={() => setViewScope("personal")}
            className={"px-3 py-1 text-xs " + (personalMode ? "bg-primary text-primary-foreground" : "bg-white hover:bg-muted text-foreground")}
            data-testid="cal-view-scope-personal"
          >My schedule</button>
          <button
            type="button"
            onClick={() => setViewScope("global")}
            className={"px-3 py-1 text-xs border-l border-border " + (!personalMode ? "bg-primary text-primary-foreground" : "bg-white hover:bg-muted text-foreground")}
            data-testid="cal-view-scope-global"
          >Branch view</button>
        </div>
      ) : (
        scope.defaultMode && (
          <span className={"ml-3 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] " + (personalMode ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground bg-muted/40")}
                data-testid={`cal-view-scope-lock-${personalMode ? "personal" : "global"}`}>
            {personalMode ? "Personal view" : "Branch view"}
          </span>
        )
      )}

      {/* Zoom controls — vertical shrinks/grows the row height, horizontal
          shrinks/grows the column min-width. Range 50 – 200%. Values are
          persisted per user in localStorage. */}
      <div className="ml-auto flex items-center gap-1" data-testid="cal-zoom-controls">
        <div className="flex items-center rounded-md border border-border">
          <button
            type="button"
            onClick={() => bumpZoom("v", -0.25)}
            className="p-1.5 hover:bg-muted/60 disabled:opacity-40"
            disabled={zoomV <= 0.5}
            title="Vertical zoom out — see more hours at once"
            aria-label="Vertical zoom out"
            data-testid="cal-zoom-v-out"
          ><MoveVertical className="w-3.5 h-3.5" /><Minus className="w-2.5 h-2.5 -ml-0.5" /></button>
          <button
            type="button"
            onClick={() => bumpZoom("v", 0.25)}
            className="p-1.5 hover:bg-muted/60 disabled:opacity-40 border-l border-border"
            disabled={zoomV >= 2}
            title="Vertical zoom in — expand row height"
            aria-label="Vertical zoom in"
            data-testid="cal-zoom-v-in"
          ><MoveVertical className="w-3.5 h-3.5" /><Plus className="w-2.5 h-2.5 -ml-0.5" /></button>
        </div>
        <div className="flex items-center rounded-md border border-border">
          <button
            type="button"
            onClick={() => bumpZoom("h", -0.25)}
            className="p-1.5 hover:bg-muted/60 disabled:opacity-40"
            disabled={zoomH <= 0.5}
            title="Horizontal zoom out — fit more columns on screen"
            aria-label="Horizontal zoom out"
            data-testid="cal-zoom-h-out"
          ><MoveHorizontal className="w-3.5 h-3.5" /><Minus className="w-2.5 h-2.5 -ml-0.5" /></button>
          <button
            type="button"
            onClick={() => bumpZoom("h", 0.25)}
            className="p-1.5 hover:bg-muted/60 disabled:opacity-40 border-l border-border"
            disabled={zoomH >= 2}
            title="Horizontal zoom in — expand column width"
            aria-label="Horizontal zoom in"
            data-testid="cal-zoom-h-in"
          ><MoveHorizontal className="w-3.5 h-3.5" /><Plus className="w-2.5 h-2.5 -ml-0.5" /></button>
        </div>
      </div>
    </div>
  );
}
